/**
 * Newsletter Popup
 * Shows a subscribe popup based on configurable trigger conditions
 * and submits the email directly to Klaviyo's client-side subscribe API.
 *
 * Frequency rules (stored in localStorage / sessionStorage):
 *  - Once shown or closed → hidden for `reshowDays` days, then eligible again.
 *  - Once subscribed (form success, logged-in subscribed customer, or visitor
 *    arriving from a Klaviyo email link) → never shown again on this browser.
 *  - Never opens while the cart drawer / menu drawer / search overlay is open;
 *    waits until it closes.
 */

class NewsletterPopup {
  constructor() {
    this.popup = document.getElementById('NewsletterPopup');
    if (!this.popup) return;

    this.form = document.getElementById('NewsletterPopupForm');
    this.emailInput = document.getElementById('NewsletterPopupEmail');
    this.errorElement = document.getElementById('NewsletterPopupError');
    this.successView = document.getElementById('NewsletterPopupSuccessView');
    this.discountCopyBtn = document.getElementById('NewsletterPopupDiscountCopy');
    this.submitBtn = this.form ? this.form.querySelector('.newsletter-popup__submit') : null;

    this.trigger = this.popup.dataset.trigger || 'delay';
    this.delaySeconds = parseFloat(this.popup.dataset.delaySeconds || '5');
    this.scrollPercentage = parseFloat(this.popup.dataset.scrollPercentage || '50');
    this.storageType = this.popup.dataset.storageType || 'local';
    this.reshowDays = parseFloat(this.popup.dataset.reshowDays || '14');
    this.customerSubscribed = this.popup.dataset.customerSubscribed === 'true';
    this.klaviyoCompanyId = this.popup.dataset.klaviyoCompanyId || '';
    this.klaviyoListId = this.popup.dataset.klaviyoListId || '';
    this.forceShow = this.popup.dataset.forceShow === 'true';

    this.legacyStorageKey = 'newsletter_popup_shown';
    this.storageKey = 'newsletter_popup_state';

    this.exitIntentTriggered = false;
    this.scrollTriggered = false;
    this.waitingForOverlay = false;

    this.setup();
  }

  setup() {
    if (!this.forceShow) {
      this.migrateLegacyFlag();

      if (this.customerSubscribed || this.cameFromKlaviyoEmail()) {
        this.writeState('subscribed');
        return;
      }

      if (this.isSuppressed()) return;
    }

    this.setupCloseListeners();
    this.setupFormSubmit();
    this.setupTrigger();
  }

  /* ---------- storage ---------- */

  getStorage() {
    return this.storageType === 'session' ? sessionStorage : localStorage;
  }

  readState() {
    try {
      const raw = this.getStorage().getItem(this.storageKey);
      return raw ? JSON.parse(raw) : null;
    } catch (error) {
      return null;
    }
  }

  writeState(status) {
    if (this.forceShow) return;
    try {
      this.getStorage().setItem(this.storageKey, JSON.stringify({ status: status, ts: Date.now() }));
    } catch (error) {
      // Ignore storage errors (e.g. private browsing)
    }
  }

  // Old version stored a permanent "true" flag. Convert it to a dated
  // "dismissed" record so those visitors become eligible again after reshowDays.
  migrateLegacyFlag() {
    try {
      const storage = this.getStorage();
      if (storage.getItem(this.legacyStorageKey) === 'true') {
        if (!storage.getItem(this.storageKey)) this.writeState('dismissed');
        storage.removeItem(this.legacyStorageKey);
      }
    } catch (error) {
      // ignore
    }
  }

  isSuppressed() {
    const state = this.readState();
    if (!state) return false;
    if (state.status === 'subscribed') return true;

    // Session storage already resets per browser session; no day window needed.
    if (this.storageType === 'session') return true;

    const ageMs = Date.now() - (state.ts || 0);
    return ageMs < this.reshowDays * 24 * 60 * 60 * 1000;
  }

  cameFromKlaviyoEmail() {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.has('_kx')) return true;
      const source = (params.get('utm_source') || '').toLowerCase();
      return source.indexOf('klaviyo') !== -1;
    } catch (error) {
      return false;
    }
  }

  /* ---------- triggers ---------- */

  setupTrigger() {
    if (this.forceShow) {
      this.openPopup();
      return;
    }

    switch (this.trigger) {
      case 'scroll':
        document.addEventListener('scroll', () => this.handleScroll(), { passive: true });
        break;
      case 'exit_intent':
        document.addEventListener('mouseout', (e) => this.handleExitIntent(e));
        break;
      case 'immediate':
        this.requestOpen();
        break;
      case 'delay':
      default:
        setTimeout(() => this.requestOpen(), this.delaySeconds * 1000);
        break;
    }
  }

  handleScroll() {
    if (this.scrollTriggered) return;

    const scrollTop = window.scrollY || document.documentElement.scrollTop;
    const docHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
    const scrolled = docHeight > 0 ? (scrollTop / docHeight) * 100 : 0;

    if (scrolled >= this.scrollPercentage) {
      this.scrollTriggered = true;
      this.requestOpen();
    }
  }

  handleExitIntent(event) {
    if (this.exitIntentTriggered) return;

    if (!event.relatedTarget && event.clientY <= 0) {
      this.exitIntentTriggered = true;
      this.requestOpen();
    }
  }

  /* ---------- overlay guard (cart drawer etc.) ---------- */

  isOtherOverlayOpen() {
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && cartDrawer.classList.contains('active')) return true;

    const body = document.body.classList;
    return (
      body.contains('overflow-hidden') ||
      body.contains('overflow-hidden-mobile') ||
      body.contains('overflow-hidden-tablet') ||
      body.contains('overflow-hidden-desktop')
    );
  }

  // Open now, or wait until the cart drawer / other overlay is closed,
  // then open after a short pause so it doesn't feel like a reaction to closing it.
  requestOpen() {
    if (!this.isOtherOverlayOpen()) {
      this.openPopup();
      return;
    }
    if (this.waitingForOverlay) return;
    this.waitingForOverlay = true;

    const poll = setInterval(() => {
      if (this.isOtherOverlayOpen()) return;
      clearInterval(poll);
      setTimeout(() => {
        this.waitingForOverlay = false;
        if (this.isOtherOverlayOpen()) {
          this.requestOpen();
        } else {
          this.openPopup();
        }
      }, 3000);
    }, 1000);
  }

  /* ---------- open / close ---------- */

  setupCloseListeners() {
    const closeBtn = this.popup.querySelector('.newsletter-popup__close');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => this.closePopup());
    }

    const overlay = this.popup.querySelector('.newsletter-popup__overlay');
    if (overlay) {
      overlay.addEventListener('click', () => this.closePopup());
    }

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !this.popup.classList.contains('hidden')) {
        this.closePopup();
      }
    });
  }

  setupFormSubmit() {
    if (this.form) {
      this.form.addEventListener('submit', (e) => this.handleFormSubmit(e));
    }

    if (this.discountCopyBtn) {
      this.discountCopyBtn.addEventListener('click', () => this.handleCopyDiscountCode());
    }
  }

  openPopup() {
    if (!this.popup.classList.contains('hidden')) return;

    this.popup.classList.remove('hidden');
    this.popup.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    // Counts as "seen" even if the visitor leaves without closing it.
    this.writeState('dismissed');
  }

  closePopup() {
    this.popup.classList.add('hidden');
    this.popup.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';

    const state = this.readState();
    if (!state || state.status !== 'subscribed') this.writeState('dismissed');
  }

  /* ---------- form ---------- */

  async handleFormSubmit(event) {
    event.preventDefault();

    if (!this.emailInput.validity.valid) {
      this.showMessage(this.errorElement, this.emailInput.dataset.errorInvalid || 'Please enter a valid email address.');
      return;
    }

    const email = this.emailInput.value.trim().toLowerCase();

    this.setLoading(true);

    try {
      await this.subscribeToKlaviyo(email);
      this.writeState('subscribed');
      this.form.reset();
      this.showSuccessView();
      setTimeout(() => this.closePopup(), 3000);
    } catch (error) {
      console.error('Newsletter popup subscription error:', error);
      this.showMessage(this.errorElement, this.errorElement.dataset.errorGeneric || 'Something went wrong. Please try again.');
    } finally {
      this.setLoading(false);
    }
  }

  showSuccessView() {
    this.errorElement.classList.add('hidden');
    this.form.classList.add('hidden');
    if (this.successView) this.successView.classList.remove('hidden');
  }

  async handleCopyDiscountCode() {
    const code = this.discountCopyBtn.dataset.code || '';
    if (!code) return;

    try {
      await navigator.clipboard.writeText(code);
      this.discountCopyBtn.classList.add('is-copied');
      setTimeout(() => this.discountCopyBtn.classList.remove('is-copied'), 2000);
    } catch (error) {
      console.error('Failed to copy discount code:', error);
    }
  }

  setLoading(isLoading) {
    if (!this.submitBtn) return;

    const text = this.submitBtn.querySelector('.newsletter-popup__submit-text');
    const spinner = this.submitBtn.querySelector('.newsletter-popup__submit-spinner');

    this.submitBtn.disabled = isLoading;
    if (text) text.classList.toggle('hidden', isLoading);
    if (spinner) spinner.classList.toggle('hidden', !isLoading);
  }

  showMessage(element, message) {
    this.errorElement.classList.add('hidden');

    if (element) {
      element.textContent = message;
      element.classList.remove('hidden');
    }
  }

  async subscribeToKlaviyo(email) {
    const url = `https://a.klaviyo.com/client/subscriptions/?company_id=${this.klaviyoCompanyId}`;

    const payload = {
      data: {
        type: 'subscription',
        attributes: {
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email: email,
                subscriptions: {
                  email: {
                    marketing: {
                      consent: 'SUBSCRIBED',
                    },
                  },
                },
              },
            },
          },
        },
        relationships: {
          list: {
            data: {
              type: 'list',
              id: this.klaviyoListId,
            },
          },
        },
      },
    };

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        revision: '2026-04-15',
      },
      body: JSON.stringify(payload),
    });

    if (response.status === 202) {
      return { success: true };
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('Klaviyo API Error:', errorData);
      throw new Error(`Klaviyo API returned ${response.status}`);
    }

    return { success: true };
  }
}

document.addEventListener('DOMContentLoaded', () => new NewsletterPopup());
